import { createContext, useContext, useEffect, useState } from "react";

/**
 * Site-wide language context — used by /concierge, /government-services, /services
 * and any page that imports useLang(). Defaults to localStorage-persisted choice
 * so switching language on one page sticks across navigation.
 *
 * Note: /tltb-partnership has its own per-page picker (kept for that page's scoped
 * PDF download flow). Other pages share this global context.
 */

const LangCtx = createContext({
    lang: "en",
    setLang: () => {},
});

const STORAGE_KEY = "bula_page_lang";
const VALID = new Set(["en", "fj", "hi"]);

export function LangProvider({ children }) {
    const [lang, setLangState] = useState(() => {
        if (typeof window === "undefined") return "en";
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY);
            if (saved && VALID.has(saved)) return saved;
        } catch {
            /* ignore */
        }
        return "en";
    });

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            /* ignore */
        }
    }, [lang]);

    const setLang = (next) => {
        if (VALID.has(next)) setLangState(next);
    };

    return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export function useLang() {
    return useContext(LangCtx);
}

export const SUPPORTED_LANGS = [
    { code: "en", label: "English" },
    { code: "fj", label: "Vakaviti (Fijian)" },
    { code: "hi", label: "हिन्दी (Hindi)" },
];
